import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';
import { clearAppCaches } from '@/utils/clearAppCaches';
import { toast } from 'sonner';
import i18n from '@/i18n';

const VERSION_KEY = 'app_last_known_version';
const PERIODIC_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min


export function useCapacitorLifecycle() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return; // Ne rien faire sur web
    }

    let appStateListener: any;
    let backButtonListener: any;
    let periodicInterval: ReturnType<typeof setInterval> | null = null;
    // Dernière version déjà proposée via toast — pour ne pas re-proposer la
    // même à chaque resume tant que l'utilisateur n'a pas redémarré.
    let promptedBuildTime: number | null = null;

    /**
     * Applique réellement la mise à jour. INVARIANT : VERSION_KEY n'avance
     * QUE ici — une mise à jour détectée mais non appliquée reste donc
     * « inconnue » et sera auto-appliquée au prochain démarrage à froid.
     */
    const applyUpdate = async (serverBuildTime: number) => {
      await Preferences.set({ key: VERSION_KEY, value: serverBuildTime.toString() });
      // ✅ Nettoyer tous les caches JS avant le reload
      await clearAppCaches();
      // Hard reload avec cache-busting URL pour forcer le rechargement complet
      const baseUrl = window.location.origin + window.location.pathname;
      window.location.replace(`${baseUrl}?_forceUpdate=${serverBuildTime}`);
    };

    /**
     * `allowImmediateReload` : true uniquement au démarrage à froid (rien
     * n'est saisi). Au resume et sur l'intervalle périodique, on ne recharge
     * JAMAIS d'autorité — l'utilisateur peut être en pleine saisie (le bug
     * historique : basculer sur WhatsApp puis revenir effaçait le
     * formulaire). On affiche un toast persistant avec action « Redémarrer ».
     */
    const checkForUpdates = async ({ allowImmediateReload }: { allowImmediateReload: boolean }) => {
      try {
        console.log('[Lifecycle] Checking for app updates...');

        // 1. Récupérer la version depuis le serveur avec cache-busting agressif
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
        });

        if (!response.ok) return;

        const serverVersion = await response.json();
        const serverBuildTime = serverVersion.buildTime;

        // 2. Récupérer la version connue depuis le stockage persistant
        const { value: storedVersion } = await Preferences.get({ key: VERSION_KEY });
        const lastKnownVersion = storedVersion ? parseInt(storedVersion, 10) : null;

        console.log('[Lifecycle] Server version:', serverBuildTime, 'Local version:', lastKnownVersion);

        // 3. Si première fois, stocker et sortir
        if (lastKnownVersion === null) {
          await Preferences.set({ key: VERSION_KEY, value: serverBuildTime.toString() });
          console.log('[Lifecycle] Initial version stored:', serverBuildTime);
          return;
        }

        if (serverBuildTime <= lastKnownVersion) return;

        if (allowImmediateReload) {
          // Démarrage à froid : appliquer tout de suite, comme avant.
          console.log('[Lifecycle] 🎉 New version detected at startup! Applying...');
          toast.info(i18n.t('capacitorLifecycle.newVersionAvailable', { ns: 'appui' }), {
            description: i18n.t('capacitorLifecycle.updatingInProgress', { ns: 'appui' }),
            duration: 1500,
          });
          setTimeout(() => { applyUpdate(serverBuildTime); }, 1500);
          return;
        }

        // Mi-session : proposer sans imposer (une seule fois par version).
        if (promptedBuildTime === serverBuildTime) return;
        promptedBuildTime = serverBuildTime;
        console.log('[Lifecycle] 🎉 New version detected mid-session — deferring to user');
        toast.info(i18n.t('capacitorLifecycle.newVersionAvailable', { ns: 'appui' }), {
          description: i18n.t('capacitorLifecycle.restartWhenReady', { ns: 'appui' }),
          duration: Infinity,
          action: {
            label: i18n.t('capacitorLifecycle.restart', { ns: 'appui' }),
            onClick: () => { applyUpdate(serverBuildTime); },
          },
        });
      } catch (error) {
        console.error('[Lifecycle] Error checking updates:', error);
      }
    };

    const setupListeners = async () => {
      // ✅ Vérifier la version au démarrage (non-bloquant) — seul moment où
      // un reload immédiat est acceptable : rien n'est encore saisi.
      checkForUpdates({ allowImmediateReload: true });

      // ✅ Listener pour détecter quand l'app revient au foreground
      appStateListener = await CapacitorApp.addListener('appStateChange', async (state) => {
        console.log('[Capacitor] App state changed:', state.isActive ? 'ACTIVE' : 'BACKGROUND');

        if (state.isActive) {
          // ✅ Vérifier la version au retour — SANS reload d'autorité
          checkForUpdates({ allowImmediateReload: false });

          // ✅ Déclencher l'événement immédiatement pour que l'UI commence à se mettre à jour
          window.dispatchEvent(new CustomEvent('capacitor-app-resumed'));

          console.log('[Capacitor] App resumed - checking session...');

          // Vérifier et rafraîchir la session. Le storage Preferences est
          // ASYNCHRONE : juste après un resume, getSession() peut renvoyer
          // null de façon transitoire alors que la session existe — on
          // réessaie donc avant de conclure à une vraie expiration (sinon :
          // déconnexion à tort et perte du formulaire en cours).
          let session: Session | null = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            if (attempt > 0) {
              await new Promise((r) => setTimeout(r, 1200));
              if (attempt === 2) {
                // Dernier essai : forcer une récupération via refresh.
                await supabase.auth.refreshSession().catch(() => undefined);
              }
            }
            const { data, error } = await supabase.auth.getSession();
            if (error) {
              console.error('[Capacitor] Session check error:', error);
              return;
            }
            session = data.session;
            if (session) break;
            console.warn(`[Capacitor] No session after resume (attempt ${attempt + 1}/3)`);
          }

          if (!session) {
            console.warn('[Capacitor] No session found after resume (retries exhausted)');

            // ✅ Ne pas rediriger si déjà sur une page publique d'authentification
            const currentPath = window.location.pathname;
            const isOnAuthPage = ['/login', '/set-password', '/forgot-password'].includes(currentPath);

            if (!isOnAuthPage) {
              toast.info(i18n.t('capacitorLifecycle.pleaseReconnect', { ns: 'appui' }), {
                description: i18n.t('capacitorLifecycle.sessionExpiredDescription', { ns: 'appui' })
              });
              // Rediriger vers login après 2 secondes
              setTimeout(() => {
                window.location.href = '/login';
              }, 2000);
            }
            return;
          }

          // Vérifier si le token est proche de l'expiration (< 5 minutes)
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
          const now = Date.now();
          const timeUntilExpiry = expiresAt - now;

          if (timeUntilExpiry < 5 * 60 * 1000) {
            console.log('[Capacitor] Token expiring soon, refreshing...');
            const { error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              console.error('[Capacitor] Failed to refresh session:', refreshError);
              toast.error(i18n.t('capacitorLifecycle.connectionError', { ns: 'appui' }), {
                description: i18n.t('capacitorLifecycle.refreshSessionFailed', { ns: 'appui' })
              });
            } else {
              console.log('[Capacitor] ✅ Session refreshed successfully');
            }
          } else {
            console.log('[Capacitor] ✅ Session valid, no refresh needed');
          }
        }
      });

      // ✅ Listener pour détecter les back button Android
      backButtonListener = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          CapacitorApp.exitApp();
        } else {
          window.history.back();
        }
      });
    };

    setupListeners();

    // ✅ Check périodique toutes les 5 min tant que l'app est ouverte
    periodicInterval = setInterval(() => {
      checkForUpdates({ allowImmediateReload: false });
    }, PERIODIC_CHECK_INTERVAL_MS);

    // Cleanup
    return () => {
      if (appStateListener) {
        appStateListener.remove();
      }
      if (backButtonListener) {
        backButtonListener.remove();
      }
      if (periodicInterval) {
        clearInterval(periodicInterval);
      }
    };
  }, []);
}
